import type { Track } from '@react-input/mask';

const NATIONAL_PHONE_LENGTH = 9;
const COMPLETE_MASKED_PHONE = /^\+380 \(\d{2}\)-\d{3}-\d{2}-\d{2}$/;

// The mask already contains +380 and only 9 editable slots. A copied number
// often includes that prefix, so those digits must be dropped before insert.
export const toNationalPhoneDigits = (raw: string): string => {
  let digits = raw.replace(/\D/g, '');

  if (digits.startsWith('00380')) {
    digits = digits.slice(5);
  } else if (digits.startsWith('380')) {
    digits = digits.slice(3);
  } else if (digits.startsWith('38') && digits.length === 11) {
    digits = digits.slice(2);
  } else if (digits.startsWith('80') && digits.length === 11) {
    digits = digits.slice(2);
  }

  if (digits.startsWith('0') && digits.length > NATIONAL_PHONE_LENGTH) {
    digits = digits.slice(1);
  }

  return digits.slice(0, NATIONAL_PHONE_LENGTH);
};

export const isFullPhonePaste = (raw: string): boolean => {
  if (COMPLETE_MASKED_PHONE.test(raw)) {
    return false;
  }

  return raw.replace(/\D/g, '').length > NATIONAL_PHONE_LENGTH;
};

export const trackPhonePaste: Track = ({ inputType, data }) => {
  if (inputType !== 'insert' || data.length < 2 || !isFullPhonePaste(data)) {
    return data;
  }

  return toNationalPhoneDigits(data);
};

const setNativeValue = (input: HTMLInputElement, text: string): (() => void) | null => {
  const setValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  const view = input.ownerDocument.defaultView;
  if (!setValue || !view) {
    return null;
  }

  setValue.call(input, text);
  input.setSelectionRange(text.length, text.length);
  return () => {
    input.dispatchEvent(new view.Event('input', { bubbles: true }));
  };
};

// Replaces the whole field. A caret in the middle of the mask would otherwise
// keep the prefix slots and cut the tail of the pasted number.
export const applyFullPhonePaste = (input: HTMLInputElement, text: string): boolean => {
  if (!isFullPhonePaste(text)) {
    return false;
  }

  const dispatchInput = setNativeValue(input, toNationalPhoneDigits(text));
  if (!dispatchInput) {
    return false;
  }

  dispatchInput();
  return true;
};

// Chrome autofill assigns the value and fires `input`, not `paste`. Capture the
// raw value before the mask reverts it, then re-apply after that event.
export const scheduleAutofilledPhone = (input: HTMLInputElement): void => {
  const snapshot = input.value;
  if (!isFullPhonePaste(snapshot)) {
    return;
  }

  // @react-input/mask rejects a second `input` in the same turn until its
  // selection poller advances, so wait for the next macrotask.
  const view = input.ownerDocument.defaultView;
  view?.setTimeout(() => {
    applyFullPhonePaste(input, snapshot);
  }, 0);
};
